<template>
  <div>
    <Loader v-if="loading" text="" color="black" class="hyp-loader" />
    <CalendarEventsFilters @change-filters="handleChangeFilter" />
    <v-container class="pt-3 pb-0">
      <v-row>
        <v-col class="pa-0 hyp-calendarevents-container">
          <v-container class="hyp-calendarevents-calendar-container pr-0">
            <CalendarEventsCalendar
              :items="itemsPlus"
              @get-data="getContent"
              @show-event="showEvent"
              :params="params"
              :loading="loading"
            />
          </v-container>
          <v-container class="hyp-calendarevents-timeline-container mx-0 pl-0">
            <v-card elevation="1">
              <CalendarEventsTimeline
                v-if="!loading && items.length && !selectedEvent"
                class="ma-0 pr-2"
                :items="itemsPlus"
                :pageSize="pageSize"
                :page="page"
                :infiniteLoadReset="openedTimestamp"
                :openedTimestamp="openedTimestamp"
                :scrollToTop="openedTimestamp"
                @show-event="showEvent"
              />
              <p
                v-if="(!items || !items.length) && !loading && !selectedEvent"
                class="text-center pt-10"
              >
                <b
                  >No {{ title }} available for <br />
                  selected filters and date range.</b
                >
              </p>
              <CalendarEventsItem
                v-if="selectedEvent"
                :item="selectedEvent"
                :show-back-btn="true"
                @click-close="selectedEvent = null"
              />
            </v-card>
          </v-container>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import lodashDebounce from "lodash/debounce";
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashOrderBy from "lodash/orderBy";

import { getCalendarEvents } from "@/services/calendarEvents/calendarEvents.service";
import { LABELS } from "@/common/constants/labels";

export default {
  name: "CalendarEventsCalendarHolder",
  components: {
    CalendarEventsFilters: () =>
      import("@/components/calendarEvents/CalendarEventsFilters"),
    CalendarEventsCalendar: () =>
      import("@/components/calendarEvents/CalendarEventsCalendar"),
    CalendarEventsTimeline: () =>
      import("@/components/calendarEvents/CalendarEventsTimeline"),
    CalendarEventsItem: () =>
      import("@/components/calendarEvents/CalendarEventsItem"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
    openedTimestamp: {
      type: String,
    },
  },
  data() {
    return {
      items: [],
      page: 1,
      pageSize: 10000,
      loading: false,
      selectedCategory: null,
      selectedSeverity: null,
      selectedImpactType: null,
      lastDate: null,
      selectedEvent: null,
    };
  },
  async mounted() {
    await this.getContent();
  },
  computed: {
    itemsPlus() {
      return lodashOrderBy(
        this.items.map((item) => {
          let date = lodashGet(
            item,
            "properties.formatted.eventTimelineStartDate"
          );
          if (date !== this.lastDate) {
            item.showDate = true;
            this.lastDate = date;
          } else {
            item.showDate = false;
          }
          return item;
        }),
        ["properties.formatted.eventTimelineStartDate"],
        ["asc"]
      );
    },
    title() {
      return LABELS.calendarEvents;
    },
  },
  methods: {
    showEvent(event) {
      this.selectedEvent = lodashCloneDeep(event);
    },
    async getContent(params, $state) {
      this.loading = true;
      await this.getContentDebounced(params, $state);
    },
    getContentDebounced: lodashDebounce(async function (params, $state) {
      let result;
      let dataParams;

      try {
        this.page = isNaN(params?.page) ? 1 : params?.page;
        dataParams = this.getParams(params) || {};
        result = await getCalendarEvents(dataParams);

        this.resetItems();
        this.items = result.features;
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        this.loading = false;
        result = null;
        dataParams = null;
      }
    }, 500),
    getParams() {
      let paramsCopy = lodashCloneDeep(this.params);

      paramsCopy.pageSize = this.pageSize;
      paramsCopy.pageOffset = (this.page - 1) * this.pageSize;

      paramsCopy.start = paramsCopy.start
        ? paramsCopy.start
        : new dayjs().startOf("month").startOf("day").toISOString();
      paramsCopy.end = paramsCopy.end
        ? paramsCopy.end
        : new dayjs().endOf("month").endOf("day").toISOString();
      paramsCopy.sortDirection = "asc";

      if (this.selectedCategory && this.selectedCategory.length) {
        paramsCopy.categories = this.selectedCategory;
      }
      if (this.selectedSeverity && this.selectedSeverity.length) {
        paramsCopy.severity = this.selectedSeverity;
      }
      if (this.selectedImpactType && this.selectedImpactType.length) {
        paramsCopy.impacts = this.selectedImpactType;
      }

      if (!paramsCopy.aoiId && paramsCopy.countryFilters) {
        paramsCopy.aoiId = paramsCopy.countryFilters;
      }

      // delete unneeded props
      delete paramsCopy.severityMin;
      delete paramsCopy.severityMax;
      delete paramsCopy.bounds;
      delete paramsCopy.boundsType;
      delete paramsCopy.allRegionsSelected;

      return paramsCopy;
    },
    resetItems() {
      this.items = [];
      this.lastDate = null;
    },
    resetState() {
      this.resetItems();
      this.page = 1;
    },
    async handleChangeFilter(selectedFilters) {
      this.selectedCategory = selectedFilters?.selectedCategory;
      this.selectedSeverity = selectedFilters?.selectedSeverity;
      this.selectedImpactType = selectedFilters?.selectedImpactType;
      await this.getContent();
    },
  },
  watch: {
    params: {
      async handler() {
        this.resetState();
        this.getContent();
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.selectedCategory = null;
    this.selectedSeverity = null;
    this.selectedImpactType = null;
  },
};
</script>

<style scoped>
.hyp-loader {
  position: absolute;
  right: 50px;
  top: 0;
}
.hyp-calendarevents-container {
  display: grid;
  grid-template-columns: 64.5% 34.5%;
  column-gap: 1%;
  grid-template-areas: "left right";
}
.hyp-calendarevents-timeline-container .v-card {
  min-height: 70vh;
  max-height: 70vh;
  overflow-y: auto;
}
</style>
